import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dropdown } from "react-bootstrap";
import { ThreeDot } from "react-loading-indicators";
import swal from "sweetalert";
import { format } from "date-fns";
import NoData from "../../../../assets/img/no-data.png";
import BASE_URL from "../../../../assets/global/url";
import useDecodeToken from "../../../../hooks/customHook/useDecodeToken";
import { formatToPHP } from "../../../../utils/numberFormatter";
import { useServerPagination } from "../../../../hooks/customHook/paginationHook/useServerPagination";
import { PaginationControls } from "../../../../hooks/customHook/paginationHook/usePagination";
import { getCutoffs } from "../../../../services/Cutoff/getCutoff.service";
import { generateTransactionNumber } from "../../../../services/general/generateTransactionNumber.service";
import { createRetainedNetIncome } from "../../../../services/RetainedNetIncome/createRetainedNetIncome.service";
import { getRetainedNetIncomes } from "../../../../services/RetainedNetIncome/getRetainedNetIncomes.service";
import { deleteRetainedNetIncome } from "../../../../services/RetainedNetIncome/deleteRetainedNetIncome.service";
import AddRetainedNetIncomeModal from "./AddRetainedNetIncomeModal";
import {
  ACCENT,
  ACCENT_SOFT,
  MUTED,
  ActionsToggle,
  CardLabel,
  PeriodStrip,
  cardStyle,
} from "./CardParts";

const INITIAL_FORM = {
  selectedCutoffs: [],
  transactionNumber: "",
  name: "",
  remarks: "",
};

const formatDate = (date) =>
  date ? format(new Date(date), "MMM dd, yyyy") : "--";

const getCreatorName = (record) => {
  const creator = record.retained_net_income_created_masterlist;
  return creator ? `${creator.fname} ${creator.lname}` : "--";
};

const getInitials = (record) => {
  const creator = record.retained_net_income_created_masterlist;
  if (!creator) return "?";
  return `${creator.fname?.[0] || ""}${creator.lname?.[0] || ""}`.toUpperCase();
};

function RetainedNetIncomeCard({ record, onView, onDelete }) {
  const cutoffs = record.retained_net_income_cutoffs
    .map((rnic) => rnic.cutoff)
    .filter(Boolean)
    .sort((a, b) => (a.from < b.from ? -1 : 1));

  return (
    <div
      className="card border-0 rounded-4 h-100 hover-lift transition-shadow"
      style={cardStyle()}
      onClick={onView}
    >
      <div className="card-body p-4 d-flex flex-column gap-3">
        {/* Header: transaction no., name, actions */}
        <div className="d-flex justify-content-between align-items-start">
          <div style={{ minWidth: 0 }}>
            <div
              className="text-uppercase fw-medium mb-1"
              style={{
                fontSize: "0.7rem",
                letterSpacing: "0.05em",
                color: MUTED,
              }}
            >
              {record.transaction_number}
            </div>
            <div className="fw-semibold text-dark fs-5 text-truncate">
              {record.name}
            </div>
          </div>
          <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
            <Dropdown.Toggle
              as={ActionsToggle}
              id={`rni-actions-${record.id}`}
            />
            <Dropdown.Menu className="shadow-sm border-0 rounded-3">
              <Dropdown.Item
                onClick={onView}
                className="d-flex align-items-center px-3 py-2"
              >
                <i className="bx bx-show me-2 fs-5 text-success"></i>
                <span className="text-body-secondary">View</span>
              </Dropdown.Item>
              {onDelete && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Item
                    onClick={onDelete}
                    className="d-flex align-items-center px-3 py-2 text-danger"
                  >
                    <i className="bx bx-trash me-2 fs-5"></i>
                    <span>Delete</span>
                  </Dropdown.Item>
                </>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {/* Total net income */}
        <div>
          <CardLabel className="mb-1">Total Net Income</CardLabel>
          <div
            className="fs-3 fw-bold lh-1"
            style={{ color: record.net_income < 0 ? "#d92d20" : "#079455" }}
          >
            {formatToPHP(record.net_income || 0)}
          </div>
        </div>

        {/* Period strip */}
        <PeriodStrip from={record.from} to={record.to} />

        {/* Included cutoffs */}
        <div className="d-flex flex-wrap align-items-center gap-2">
          <span className="small fw-medium me-1" style={{ color: MUTED }}>
            {cutoffs.length} cutoff{cutoffs.length === 1 ? "" : "s"}
          </span>
          {cutoffs.map((cutoff) => (
            <span
              key={cutoff.id}
              className="rounded-pill px-2 py-1 small fw-medium"
              style={{ backgroundColor: ACCENT_SOFT, color: ACCENT }}
            >
              {cutoff.name}
            </span>
          ))}
        </div>

        {/* Footer: creator and date */}
        <div
          className="mt-auto pt-3 d-flex align-items-center justify-content-between small"
          style={{ borderTop: "1px solid #f0f1f5" }}
        >
          <div
            className="d-flex align-items-center gap-2"
            style={{ minWidth: 0 }}
          >
            <span
              className="rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold flex-shrink-0"
              style={{
                width: 26,
                height: 26,
                fontSize: "0.7rem",
                backgroundColor: ACCENT_SOFT,
                color: ACCENT,
              }}
            >
              {getInitials(record)}
            </span>
            <span className="text-truncate text-body-secondary">
              {getCreatorName(record)}
            </span>
          </div>
          <span className="text-nowrap" style={{ color: MUTED }}>
            {formatDate(record.createdAt)}
          </span>
        </div>
      </div>
    </div>
  );
}

function RetainedNetIncome({ authrztn, viewSwitch }) {
  const userLoggedID = useDecodeToken();
  const navigate = useNavigate();

  // Retained Net Income table (server-side paginated)
  const pagination = useServerPagination(
    `${BASE_URL}/retained_net_income/getRetainedNetIncomesPaginated`,
    10,
  );

  // Search (name, transaction no. or cutoff name), debounced like the
  // cutoff search
  const [searchText, setSearchText] = useState("");
  const debounceTimer = useRef(null);

  useEffect(() => () => clearTimeout(debounceTimer.current), []);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      pagination.updateParams({ searchText: value.trim() });
    }, 600);
  };

  // Every active record, used only to hide cutoffs that are already taken
  const [records, setRecords] = useState([]);

  // Add Retained Net Income modal
  const [showAdd, setShowAdd] = useState(false);
  const [validated, setValidated] = useState(false);
  const [cutoffData, setCutoffData] = useState([]);
  const [isCutoffLoading, setIsCutoffLoading] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);

  // isSaving drives the UI; the ref blocks a second submit immediately,
  // before the state update has re-rendered.
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const fetchCutoffs = async () => {
    setIsCutoffLoading(true);
    try {
      const data = await getCutoffs();
      setCutoffData(data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsCutoffLoading(false);
    }
  };

  const fetchRecords = async () => {
    try {
      const data = await getRetainedNetIncomes();
      setRecords(data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchCutoffs();
    fetchRecords();
  }, []);

  // Cutoffs already used by an existing Retained Net Income can't be reused
  const usedCutoffIds = new Set(
    records.flatMap((item) =>
      item.retained_net_income_cutoffs.map((rnic) => rnic.cutoff_id),
    ),
  );

  const cutoffOptions = cutoffData
    .filter((cutoff) => !usedCutoffIds.has(cutoff.id))
    .map((cutoff) => ({
      value: cutoff.id,
      label: cutoff.name,
      from: cutoff.from,
      to: cutoff.to,
    }));

  const handleShowAdd = async () => {
    setValidated(false);
    setForm(INITIAL_FORM);
    setShowAdd(true);

    try {
      const code = await generateTransactionNumber("RNI");
      handleFieldChange("transactionNumber", code);
    } catch (error) {
      console.log(error);
      swal({
        icon: "error",
        title: "Something went wrong",
        text: "Unable to generate a transaction number.",
      });
    }
  };

  const handleClose = () => {
    if (isSavingRef.current) return;
    setShowAdd(false);
    setValidated(false);
    setForm(INITIAL_FORM);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSavingRef.current) return;
    setValidated(true);

    if (
      !form.transactionNumber ||
      form.selectedCutoffs.length === 0 ||
      !form.name.trim()
    ) {
      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill in the red text fields.",
      });
      return;
    }

    swal({
      title: "Confirm Action",
      text: "Are you sure you want to add this Retained Net Income?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed || isSavingRef.current) return;
      isSavingRef.current = true;
      setIsSaving(true);

      const payload = {
        cutoff_ids: form.selectedCutoffs.map((cutoff) => cutoff.value),
        transaction_number: form.transactionNumber,
        name: form.name.trim(),
        remarks: form.remarks.trim(),
        userLoggedID,
      };

      let saved = false;
      try {
        await createRetainedNetIncome(payload);
        saved = true;
      } catch (error) {
        console.log(error);
        swal({
          icon: "error",
          title: "Something went wrong",
          text:
            error.response?.data?.message ||
            "Unable to save the Retained Net Income.",
        });
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }

      if (saved) {
        swal({
          icon: "success",
          title: "Retained Net Income added",
          text: "The Retained Net Income has been saved.",
        });
        handleClose();
        fetchRecords();
        pagination.refreshData();
      }
    });
  };

  const handleDelete = (record) => {
    swal({
      title: "Confirm Action",
      text: `Are you sure you want to delete "${record.name}"? Its cutoffs will be available again for another Retained Net Income.`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;

      try {
        await deleteRetainedNetIncome({ id: record.id, userLoggedID });
      } catch (error) {
        console.log(error);
        swal({
          icon: "error",
          title: "Something went wrong",
          text:
            error.response?.data?.message ||
            "Unable to delete the Retained Net Income.",
        });
        return;
      }

      swal({
        icon: "success",
        title: "Retained Net Income deleted",
        timer: 2000,
        button: false,
      });
      fetchRecords();
      // Step back a page when the last card on it was deleted
      if (pagination.data.length === 1 && pagination.currentPage > 1) {
        pagination.setCurrentPage(pagination.currentPage - 1);
      } else {
        pagination.refreshData();
      }
    });
  };

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      <div className="w-100 p-2 mb-1 d-flex flex-row justify-content-between">
        <div className="d-flex flex-column title-custom">
          <span className="fs-3 text-uppercase">Retained Net Income</span>
        </div>
        <div className="add-buttons-container">
          {authrztn.includes("Monthly-Add") && (
            <Button
              className="btn d-flex align-items-center title-button"
              variant="primary"
              onClick={handleShowAdd}
            >
              <i className="bx bx-plus fs-5"></i> Add Retained Net Income
            </Button>
          )}
        </div>
      </div>

      {viewSwitch}

      <div className="w-100 pt-2 px-4">
        <input
          type="text"
          className="form-control"
          placeholder="Search by name, transaction no. or cutoff"
          value={searchText}
          onChange={handleSearch}
        />
      </div>

      <div className="w-100 p-4">
        {pagination.loading ? (
          <div className="p-4 d-flex justify-content-center">
            <ThreeDot color="#0d6efd" size="medium" text="" textColor="" />
          </div>
        ) : pagination.data.length === 0 ? (
          <div className="p-4 d-flex flex-column align-items-center">
            <img src={NoData} alt="No Data" style={{ width: "150px" }} />
            <span className="text-muted mt-2">
              {searchText.trim()
                ? `No Retained Net Income matches "${searchText.trim()}".`
                : "No Retained Net Income yet."}
            </span>
          </div>
        ) : (
          <div className="row g-4">
            {pagination.data.map((record) => (
              <div className="col-md-6" key={record.id}>
                <RetainedNetIncomeCard
                  record={record}
                  onView={() =>
                    navigate(`/accounting/retained-net-income/${record.id}`)
                  }
                  onDelete={
                    authrztn.includes("Retained-Delete")
                      ? () => handleDelete(record)
                      : null
                  }
                />
              </div>
            ))}
          </div>
        )}
        <PaginationControls {...pagination} />
      </div>

      <AddRetainedNetIncomeModal
        show={showAdd}
        onClose={handleClose}
        onSubmit={handleSubmit}
        form={form}
        onFieldChange={handleFieldChange}
        validated={validated}
        cutoffOptions={cutoffOptions}
        isLoading={isCutoffLoading}
        isSaving={isSaving}
      />
    </div>
  );
}

export default RetainedNetIncome;
